import axios from "axios";

import { apiConfig } from "../config/authConfig";
import { CredentialManager } from "../classes/credentialManager";

import { IEmployee, IEmployeeDetails, IEmployeeLight, IEmployeePermissions } from "../types/employee";


export namespace EmployeeService {
	const credentialManager = CredentialManager.getInstance();

	/**********
	 * Convert one api response object to one Employee.
	 * @param data Employee response data from an API.
	 **********/
	export function mapEmployee(data: any) {
		let item: IEmployee = {
			id: data.id,
			email: data.email,
			permissions: data.permissions,
			tilsAccess: data.tilsAccess,
			details: data.details,
		}

		return item;
	}

	/**********
	 * Convert an array of api response objects to an array of Customers.
	 * @param data Customer data from an API response.
	 **********/
	export function mapEmployees(data: any) {
		let items: IEmployee[] = [];

		if(Array.isArray(data) && data.length > 0){
			data.map((item: any) => {
				items.push(mapEmployee(item));
			})
		}

		return items;
	}

	/**********
	 * Convert one api response object to one Employee.
	 * @param data Employee response data from an API.
	 **********/
	export function mapEmployeeLight(data: any) {
		let employee: IEmployeeLight = {
			id: data.id,
			firstName: data.firstName,
			lastName: data.lastName,
			username: data.username,
			phone: data.phone,
			email: data.email,
			status: data.status,
		}

		return employee;
	}

	/**********
	 * Convert an array of api response objects to an array of Employees.
 	* @param data Employee data from an API response.
 	**********/
	export function mapEmployeeLightList(data: any) {
		let employees: IEmployeeLight[] = [];

		if (Array.isArray(data) && data.length > 0) {
			data.map((item: any) => {
				employees.push(mapEmployeeLight(item));
			})
		}

		return employees;
	}
	
	/**********
	 * Gets all employees.
	 **********/
	export async function getEmployees() {
		const employeeResponse = axios.get(
			apiConfig.default + "/employee/all",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return employeeResponse;
	}

	/**********
	 * Gets an employee by id.
	 * @param employeeId The id of the employee.
	 **********/
	export async function getEmployee(employeeId: string) {
		const employeeResponse = axios.get(
			apiConfig.default + "/employee/" + employeeId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return employeeResponse;
	}

	/**********
	 * Gets current user/employee's permissions, uses token to identify the employee.
	 **********/
	export async function getCurrentUser() {
		console.log("Begin EmployeeService.getCurrentUser: " + apiConfig.default + "/employee/user");
		const employeeResponse = axios.get(
			apiConfig.default + "/employee/user",
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return employeeResponse;
	}

	/**********
	 * Add a new employee starting with details.
	 * @param details The employees's details.
	 **********/
	export async function addEmployee(details: IEmployeeDetails) {
		const apiResponse = axios.post(
			apiConfig.default + "/employee",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update an employee's details.
	 * @param employeeId The employees's id.
	 * @param details The employees's details.
	 **********/
	export async function setDetails(employeeId: string, details: IEmployeeDetails) {
		//if(details.title == "") details.title = undefined;

		const apiResponse = axios.put(
			apiConfig.default + "/employee/" + employeeId + "/details",
			details,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Update an employee's permissions.
	 * @param employeeId The employees's id.
	 * @param permissions The employees's permissions.
	 **********/
	export async function setPermissions(employeeId: number, permissions: IEmployeePermissions) {
		const apiResponse = axios.put(
			apiConfig.default + "/employee/" + employeeId + "/permissions",
			permissions,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Grant an employee access to a customer in TILS.
	 * @param employeeId The employee's id.
	 * @param customerId The customer's id.
	 **********/
	export async function grantTilsAccess(employeeId: string, customerId: string) {
		const apiResponse = axios.put(
			apiConfig.default + "/employee/" + employeeId + "/tils/" + customerId,
			{customerId},
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`,
					'Content-Type': 'application/json'
				},
			}
		)

		return apiResponse;
	}

	/**********
	 * Revoke an employee access to a customer in TILS.
	 * @param employeeId The employee's id.
	 * @param customerId The customers's id.
	 **********/
	export async function revokeTilsAccess(employeeId: string, customerId: string) {
		const apiResponse = axios.delete(
			apiConfig.default + "/employee/" + employeeId + "/tils/" + customerId,
			{
				headers: {
					'Authorization': `Bearer ${await credentialManager.getAccessToken()}`
				},
			}
		)

		return apiResponse;
	}

}
